import { useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../../services/api'

/**
 * Programar que una factura se repita.
 *
 * **Solo la programación.** Lo que se factura sale de la factura que se toma como modelo: sus N
 * conceptos con todos sus campos, retenciones, impuestos, CFDI relacionados y leyendas. La primera
 * versión de esta pantalla tenía su propio formulario de un concepto, y el owner lo rechazó con
 * razón: «¿qué tal si estoy repitiendo una venta de flores?».
 *
 * La factura se repite **tal cual**. Si el contador quiere que el concepto diga el mes, escribe
 * `{{MES_NOMBRE}}` en la descripción de su factura; el sistema no lo agrega por su cuenta.
 */
export default function ProgramarRecurrencia({ factura, onHide, onSuccess }) {
  const [datos, setDatos] = useState({
    nombre: `Recurrente de ${factura.invoice_number}`,
    periodicidad: 'mensual',
    veces: 12,
    borradores_por_adelantado: 1,
    dia_programado: new Date().getDate(),
    hora_programada: '06:00',
    vigencia_inicio: new Date().toISOString().slice(0, 10),
    modo_timbrado: 'manual',
    envia_email: true,
  })
  const [errores, setErrores] = useState({})

  const cambiar = (e) => setDatos((p) => ({ ...p, [e.target.name]: e.target.value }))

  // El catálogo viene del backend: duplicarlo aquí dejaría invisible una periodicidad nueva.
  const { data: periodicidades } = useQuery(
    'periodicidades',
    async () => (await api.get('/financial/recurrencias/periodicidades/')).data
  )

  const crear = useMutation(
    async () => (await api.post('/financial/recurrencias/', {
      cliente: factura.client,
      configuracion: factura.configuracion,
      receptor: factura.receptor_cliente || factura.client,
      factura_modelo: factura.id,
      ...datos,
      veces: datos.veces === '' ? null : Number(datos.veces),
      borradores_por_adelantado: Number(datos.borradores_por_adelantado),
      dia_programado: Number(datos.dia_programado),
      datos: {},
    })).data,
    {
      onSuccess: () => {
        toast.success('Recurrencia creada. Prepara sus borradores desde la pestaña Recurrentes.')
        onSuccess?.()
        onHide()
      },
      onError: (e) => {
        setErrores(e.response?.data || {})
        toast.error('No se pudo programar la recurrencia')
      },
    }
  )

  const total = datos.veces === '' ? null : Number(datos.veces)
  const porAdelantado = Number(datos.borradores_por_adelantado) || 1
  const excede = total !== null && porAdelantado > total
  const mensajeError = errores.detail || errores.factura_modelo || errores.non_field_errors

  return (
    <Modal show onHide={onHide} size="lg">
      <Form onSubmit={(e) => { e.preventDefault(); setErrores({}); crear.mutate() }}>
        <Modal.Header closeButton>
          <Modal.Title>Repetir la factura {factura.invoice_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mensajeError && <Alert variant="danger">{String(mensajeError)}</Alert>}

          {/* Lo que se va a repetir, para que quede claro que NO se captura aquí. */}
          <Table size="sm" className="mb-3">
            <tbody>
              <tr>
                <td className="text-muted" style={{ width: '38%' }}>Se repetirá</td>
                <td><strong>{factura.invoice_number}</strong> — tal cual, con sus conceptos</td>
              </tr>
              <tr>
                <td className="text-muted">Receptor</td>
                <td>{factura.receptor_razon_social || factura.client_name || '—'}</td>
              </tr>
              <tr>
                <td className="text-muted">Total de cada factura</td>
                <td>
                  ${Number(factura.total || 0).toLocaleString('es-MX',
                                                              { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </Table>

          <Form.Group className="mb-3">
            <Form.Label>Nombre de la recurrencia *</Form.Label>
            <Form.Control name="nombre" value={datos.nombre} onChange={cambiar} required />
            <Form.Text className="text-muted">Para reconocerla en la lista.</Form.Text>
          </Form.Group>

          <Row>
            <Col md={4} className="mb-3">
              <Form.Label>Cada</Form.Label>
              <Form.Select name="periodicidad" value={datos.periodicidad} onChange={cambiar}>
                {(periodicidades || [{ clave: 'mensual', etiqueta: 'Mensual' }]).map((p) => (
                  <option key={p.clave} value={p.clave}>{p.etiqueta}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Veces</Form.Label>
              <Form.Control type="number" min={1} name="veces" value={datos.veces}
                            onChange={cambiar} placeholder="vacío = sin fin" />
              <Form.Text className="text-muted">Facturas en total.</Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Preparar por adelantado</Form.Label>
              <Form.Control type="number" min={1} name="borradores_por_adelantado"
                            value={datos.borradores_por_adelantado} onChange={cambiar} />
              <Form.Text className="text-muted">Listas a la vez para revisarlas.</Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Día</Form.Label>
              <Form.Control type="number" min={1} max={31} name="dia_programado"
                            value={datos.dia_programado} onChange={cambiar} />
              <Form.Text className="text-muted">Si el mes no lo tiene, se avisa.</Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Hora</Form.Label>
              <Form.Control type="time" name="hora_programada" value={datos.hora_programada}
                            onChange={cambiar} />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Primer periodo</Form.Label>
              <Form.Control type="date" name="vigencia_inicio" value={datos.vigencia_inicio}
                            onChange={cambiar} />
            </Col>
          </Row>

          {excede && (
            <Alert variant="warning" className="py-2 small">
              Pides {porAdelantado} borradores por adelantado de una recurrencia de {total}:
              se crearán {total}.
            </Alert>
          )}
          {errores.borradores_por_adelantado && (
            <Alert variant="danger" className="py-2 small">
              {String(errores.borradores_por_adelantado)}
            </Alert>
          )}

          <Form.Check type="checkbox" id="rec-auto-factura"
                      label="Timbrar automáticamente al llegar la fecha"
                      checked={datos.modo_timbrado === 'automatico'}
                      onChange={(e) => setDatos((p) => ({
                        ...p, modo_timbrado: e.target.checked ? 'automatico' : 'manual' }))} />
          <Form.Text className="text-muted">
            Sin marcar, los borradores se preparan y tú decides cuándo timbrarlos.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={crear.isLoading}>
            {crear.isLoading ? 'Programando…' : 'Programar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
